import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import {
    panDomain,
    sameDomain,
    zoomDomain,
} from '../../components/charts/chartZoom';
import { useContainerHeight } from '../../hooks/useContainerHeight';
import { useContainerWidth } from '../../hooks/useContainerWidth';
import { formatSpan, lifetimeText, type Lifetime } from '../lifetime';
import {
    ChartValuesStrip,
    ChartValuesStripPlaceholder,
} from './ChartValuesStrip';
import { LifetimeBar } from './LifetimeBar';
import {
    compactMargins,
    DESKTOP_MARGINS,
    lifetimeReserve,
    PANEL_MARGIN,
    tickLabelWidths,
    widest,
} from './margins';
import { Panel, TIME_AXIS_HEIGHT } from './Panel';
import { planPanels, withPending } from './placement';
import type { ChartSeries, PanelId, SeriesMoves } from './seriesTypes';
import {
    DEFAULT_PROBABILITY_HEIGHT,
    PANEL_GAP,
    panelsToFold,
    stackLayout,
} from './stackLayout';
import { useCompactChart } from './useCompactChart';
import { ViewportContext, type ChartViewport } from './viewport';

const COMMIT_DELAY_MS = 200;
const FULL_RANGE_TOLERANCE_MS = 1000;

interface ChartStackProps {
    series: readonly ChartSeries[];
    moves?: SeriesMoves;
    onMovesChange?: (moves: SeriesMoves) => void;
    bounds: [Date, Date];
    range: [Date, Date] | null;
    onRangeChange: (range: [Date, Date] | null) => void;
    availableHeight?: number | null;
    loading?: boolean;
    hiddenPanels?: ReadonlySet<PanelId>;
    onHidePanel?: (panel: PanelId) => void;
    panelExtras?: Readonly<Record<string, ReactNode>>;
    /**
     * Content over the plot area of a panel (a loading state, say). It does not
     * take the pointer: the chart below it still pans and reads the cursor.
     */
    panelOverlays?: Readonly<Record<string, ReactNode>>;
    panelTitles?: Readonly<Record<string, string>>;
    /**
     * Content in its own row above a panel, for what does not fit the one-line
     * panel header. The row takes its height from the panel below it.
     */
    panelStrips?: Readonly<Record<string, ReactNode>>;
    lifetime?: Lifetime | null;
    /** "1D", "All": the pressed range button, for the phone values strip. */
    rangeLabel?: string | null;
    /** The least height of the main panel; lower than the default only in full screen on a short window. */
    minMainHeight?: number;
    /**
     * Take the space the parent leaves (the parent is a flex column with a
     * fixed height) instead of `availableHeight`: the browser lays out the
     * toolbar and the values strip, the panels get what remains. Until the
     * panels' box is measured, `availableHeight` is drawn.
     */
    fill?: boolean;
    /**
     * Panels that fold away, as if the page had hidden them, while `fill` is
     * on and the main panel cannot keep `minMainHeight` beside them (a phone
     * on its side). The page offers them as chips and turns them on again by
     * no longer listing them here.
     */
    foldPanels?: ReadonlySet<PanelId>;
    /** The panels folded away now, whenever that changes. */
    onFolded?: (folded: ReadonlySet<PanelId>) => void;
    /**
     * Panels whose data is on its way. Each is drawn as a placeholder at the
     * size it will have once its data is in, so nothing moves when it lands.
     */
    pendingPanels?: readonly PanelId[];
    /**
     * The height of the strip above a pending panel (the clock's header),
     * used until the real strip is there to be measured.
     */
    pendingStripHeights?: Readonly<Record<string, number>>;
    /**
     * Below 900 px: the rows the values strip will hold, drawn as a
     * placeholder while the main panel is pending.
     */
    pendingValueRows?: number;
}

const NO_MOVES: SeriesMoves = {};
const NO_HIDDEN: ReadonlySet<PanelId> = new Set();
const NO_EXTRAS: Readonly<Record<string, ReactNode>> = {};
const NO_TITLES: Readonly<Record<string, string>> = {};
const NO_OVERLAYS: Readonly<Record<string, ReactNode>> = {};
const NO_STRIPS: Readonly<Record<string, ReactNode>> = {};
const NO_HEIGHTS: Readonly<Record<string, number>> = {};
const NO_FOLD: ReadonlySet<PanelId> = new Set();
const NO_PANELS: readonly PanelId[] = [];

// A panel whose data is on its way, at the height it will have (its strip
// included).
function PanelPlaceholder({ id, height }: { id: PanelId; height: number }) {
    return (
        <Skeleton
            variant="rounded"
            width="100%"
            height={height}
            data-testid={`panel-placeholder-${id}`}
        />
    );
}

function panelGapSx(index: number) {
    return {
        mt: index > 0 ? `${PANEL_GAP}px` : 0,
        borderTop: index > 0 ? 1 : 0,
        borderColor: 'divider',
    };
}

/**
 * The chart before its time range is known (the event has not arrived): the
 * panels it will have, as placeholders laid out as ChartStack lays them out.
 */
export function ChartStackPlaceholder({
    panels,
    availableHeight = null,
    stripHeights = NO_HEIGHTS,
    valueRows = 0,
}: {
    panels: readonly PanelId[];
    availableHeight?: number | null;
    stripHeights?: Readonly<Record<string, number>>;
    valueRows?: number;
}) {
    const compact = useCompactChart();
    const layout = stackLayout(panels, availableHeight, stripHeights);
    return (
        <Box
            data-testid="chart-stack-placeholder"
            sx={{ width: '100%', minWidth: 0, position: 'relative' }}
        >
            {compact && <ChartValuesStripPlaceholder rows={valueRows} />}
            {panels.map((id, i) => (
                <Box key={id} sx={panelGapSx(i)}>
                    <PanelPlaceholder
                        id={id}
                        height={
                            (stripHeights[id] ?? 0) +
                            layout.boxes[i].height +
                            (i === panels.length - 1 ? TIME_AXIS_HEIGHT : 0)
                        }
                    />
                </Box>
            ))}
        </Box>
    );
}

// A row above a panel. It reports its height, which is not known up front
// (its content wraps), so the layout can leave room for it.
function PanelStrip({
    id,
    onHeight,
    children,
}: {
    id: string;
    onHeight: (id: string, height: number) => void;
    children: ReactNode;
}) {
    const ref = useRef<HTMLDivElement>(null);
    // The plot's side margins hold its axis labels; a strip has none, and
    // below 900 px they would squeeze its (44 px) controls into a column.
    const compact = useCompactChart();
    // Before paint, so the first frame has the plot at its final height.
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        onHeight(id, el.getBoundingClientRect().height);
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(([entry]) =>
            onHeight(id, entry.contentRect.height)
        );
        observer.observe(el);
        return () => {
            observer.disconnect();
            onHeight(id, 0);
        };
    }, [id, onHeight]);
    return (
        <Box ref={ref} data-testid={`chart-strip-${id}`}>
            <Box
                sx={{
                    display: 'flex',
                    pl: compact ? 0 : `${PANEL_MARGIN.left}px`,
                    pr: compact ? 0 : `${PANEL_MARGIN.right}px`,
                    py: 0.5,
                    minWidth: 0,
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

export function ChartStack({
    series,
    moves = NO_MOVES,
    onMovesChange,
    bounds,
    range,
    onRangeChange,
    availableHeight = null,
    loading = false,
    hiddenPanels = NO_HIDDEN,
    onHidePanel,
    panelExtras = NO_EXTRAS,
    panelOverlays = NO_OVERLAYS,
    panelTitles = NO_TITLES,
    panelStrips = NO_STRIPS,
    lifetime = null,
    rangeLabel = null,
    minMainHeight,
    fill = false,
    foldPanels = NO_FOLD,
    onFolded,
    pendingPanels = NO_PANELS,
    pendingStripHeights = NO_HEIGHTS,
    pendingValueRows = 0,
}: ChartStackProps) {
    const { ref, width } = useContainerWidth();
    const panelsBox = useContainerHeight(fill);
    const compact = useCompactChart();
    const [live, setLive] = useState<[Date, Date] | null>(null);
    const [cursor, setCursorState] = useState<{
        t: number;
        panel: PanelId | null;
    } | null>(null);
    const commitTimer = useRef<ReturnType<typeof setTimeout>>();
    const lastCommitted = useRef<[Date, Date] | null>(null);

    const domain = live ?? range ?? bounds;
    const domainRef = useRef(domain);
    domainRef.current = domain;
    const boundsRef = useRef(bounds);
    boundsRef.current = bounds;
    const onRangeChangeRef = useRef(onRangeChange);
    onRangeChangeRef.current = onRangeChange;

    const rangeStart = range?.[0].getTime() ?? null;
    const rangeEnd = range?.[1].getTime() ?? null;
    useEffect(() => {
        const committed = lastCommitted.current;
        const external =
            rangeStart === null || rangeEnd === null || committed === null
                ? rangeStart !== null || committed !== null
                : committed[0].getTime() !== rangeStart ||
                  committed[1].getTime() !== rangeEnd;
        if (external) {
            lastCommitted.current = null;
            setLive(null);
        }
    }, [rangeStart, rangeEnd]);

    useEffect(() => () => clearTimeout(commitTimer.current), []);

    const apply = useCallback((next: [Date, Date]) => {
        // A gesture can zoom and pan inside one event: the second call must
        // start from the first one's result, not from the last render.
        domainRef.current = next;
        setLive(next);
        clearTimeout(commitTimer.current);
        commitTimer.current = setTimeout(() => {
            const full = sameDomain(
                next,
                boundsRef.current,
                FULL_RANGE_TOLERANCE_MS
            );
            lastCommitted.current = full ? null : next;
            if (full) setLive(null);
            onRangeChangeRef.current(full ? null : next);
        }, COMMIT_DELAY_MS);
    }, []);

    const setCursor = useCallback((t: number | null, panel?: PanelId) => {
        setCursorState(t === null ? null : { t, panel: panel ?? null });
    }, []);

    const zoomAt = useCallback(
        (t: number, factor: number) =>
            apply(
                zoomDomain(
                    domainRef.current,
                    new Date(t),
                    factor,
                    boundsRef.current
                )
            ),
        [apply]
    );

    const panBy = useCallback(
        (deltaMs: number) =>
            apply(panDomain(domainRef.current, deltaMs, boundsRef.current)),
        [apply]
    );

    const reset = useCallback(() => {
        clearTimeout(commitTimer.current);
        lastCommitted.current = null;
        setLive(null);
        onRangeChangeRef.current(null);
    }, []);

    const moveSeries = useMemo(() => {
        if (!onMovesChange) return null;
        return (seriesId: string, target: PanelId) => {
            const home = series.find(s => s.id === seriesId)?.home;
            const next: Record<string, PanelId> = { ...moves };
            if (target === home) delete next[seriesId];
            else next[seriesId] = target;
            onMovesChange(next);
        };
    }, [onMovesChange, series, moves]);

    const viewport = useMemo<ChartViewport>(
        () => ({
            domain,
            bounds,
            cursor: cursor?.t ?? null,
            cursorPanel: cursor?.panel ?? null,
            setCursor,
            zoomAt,
            panBy,
            reset,
            allSeries: series,
            moves,
            moveSeries,
            hidePanel: onHidePanel ?? null,
            panelExtras,
            panelOverlays,
            panelTitles,
        }),
        [
            domain,
            bounds,
            cursor,
            setCursor,
            zoomAt,
            panBy,
            reset,
            series,
            moves,
            moveSeries,
            onHidePanel,
            panelExtras,
            panelOverlays,
            panelTitles,
        ]
    );

    const unfolded = useMemo(
        () =>
            planPanels(series, moves).filter(
                p => p.id === 'probability' || !hiddenPanels.has(p.id)
            ),
        [series, moves, hiddenPanels]
    );
    const [stripHeights, setStripHeights] =
        useState<Readonly<Record<string, number>>>(NO_HEIGHTS);
    // The last height each strip was seen at: a folded panel has no strip on
    // screen to measure, and the decision to fold it must not flip because
    // the strip went away with it.
    const seenStrips = useRef<Record<string, number>>({});
    const setStripHeight = useCallback((id: string, height: number) => {
        if (height > 0) seenStrips.current[id] = height;
        setStripHeights(prev =>
            prev[id] === height ? prev : { ...prev, [id]: height }
        );
    }, []);
    // The panels the page offered to fold that have to go: the box is the
    // browser's (it does not depend on what is in it), so this cannot flap.
    const folded = useMemo<readonly PanelId[]>(() => {
        if (!fill || panelsBox.height <= 0 || foldPanels.size === 0) return [];
        const strips: Record<string, number> = {};
        for (const p of unfolded) {
            if (panelStrips[p.id]) {
                strips[p.id] =
                    stripHeights[p.id] || seenStrips.current[p.id] || 0;
            }
        }
        return panelsToFold(
            unfolded.map(p => p.id),
            panelsBox.height - TIME_AXIS_HEIGHT,
            strips,
            foldPanels,
            minMainHeight
        );
    }, [
        fill,
        panelsBox.height,
        foldPanels,
        unfolded,
        panelStrips,
        stripHeights,
        minMainHeight,
    ]);
    const foldedKey = folded.join('\n');
    const onFoldedRef = useRef(onFolded);
    onFoldedRef.current = onFolded;
    // Before paint, so the page's chip is there in the first frame.
    useLayoutEffect(() => {
        onFoldedRef.current?.(
            new Set<PanelId>(
                foldedKey === '' ? [] : (foldedKey.split('\n') as PanelId[])
            )
        );
    }, [foldedKey]);
    const plan = useMemo(
        () => unfolded.filter(p => !folded.includes(p.id)),
        [unfolded, folded]
    );
    // The panels whose data is on its way. (Not in full screen: it opens on a
    // loaded chart.)
    const pending = useMemo(
        () =>
            fill
                ? NO_PANELS
                : pendingPanels.filter(
                      id => id === 'probability' || !hiddenPanels.has(id)
                  ),
        [fill, pendingPanels, hiddenPanels]
    );
    // A pending panel stays a placeholder also when part of its data is in:
    // it fills once, when the page says all of it is there.
    const planById = useMemo(
        () =>
            new Map(
                plan.filter(p => !pending.includes(p.id)).map(p => [p.id, p])
            ),
        [plan, pending]
    );
    // The panels drawn: the plan's and, in their places, the pending ones.
    const stackIds = useMemo(
        () =>
            withPending(
                plan.map(p => p.id),
                pending
            ),
        [plan, pending]
    );
    const shownStrips = useMemo(() => {
        const heights: Record<string, number> = {};
        for (const id of stackIds) {
            if (planById.has(id)) {
                if (panelStrips[id]) heights[id] = stripHeights[id] ?? 0;
            } else if (pendingStripHeights[id]) {
                heights[id] = pendingStripHeights[id];
            }
        }
        return heights;
    }, [stackIds, planById, panelStrips, stripHeights, pendingStripHeights]);
    // What the panels' box holds beyond the panels' own heights: the time
    // axis under the last one, and the 1 px rule above each later one.
    const filled =
        fill && panelsBox.height > 0
            ? Math.max(
                  0,
                  panelsBox.height - TIME_AXIS_HEIGHT - (stackIds.length - 1)
              )
            : null;
    const budget = filled ?? availableHeight;
    const layout = useMemo(
        () => stackLayout(stackIds, budget, shownStrips, minMainHeight),
        [stackIds, budget, shownStrips, minMainHeight]
    );
    // When the panels stop at their least heights they are taller than the
    // box the browser gave them, and the time axis lies this far under its
    // bottom edge (the card scrolls). The lifetime bar sits on the axis.
    const overflow =
        filled === null
            ? 0
            : Math.max(
                  0,
                  layout.totalHeight +
                      TIME_AXIS_HEIGHT +
                      (stackIds.length - 1) -
                      panelsBox.height
              );
    const mainSeries = useMemo(
        () => planById.get('probability')?.series ?? [],
        [planById]
    );

    const emptyHeight = availableHeight ?? DEFAULT_PROBABILITY_HEIGHT;
    const zoomed = !sameDomain(domain, bounds, FULL_RANGE_TOLERANCE_MS);

    // One set of margins for every panel, so the plots, the time axis and the
    // lifetime bar line up. From 900 px: 56 px each side, which holds the name
    // chips on the right. Below: what the y labels of any panel need (the full
    // range sets the floor, so the plot does not shift as the range moves; a
    // zoom that makes the ticks finer widens it) and a few pixels.
    const boundsStart = bounds[0].getTime();
    const boundsEnd = bounds[1].getTime();
    const domainStart = domain[0].getTime();
    const domainEnd = domain[1].getTime();
    const fullWidths = useMemo(
        () =>
            compact
                ? tickLabelWidths(plan, [boundsStart, boundsEnd], Date.now())
                : null,
        [compact, plan, boundsStart, boundsEnd]
    );
    const shownWidths = useMemo(
        () =>
            compact && zoomed
                ? tickLabelWidths(plan, [domainStart, domainEnd], Date.now())
                : null,
        [compact, zoomed, plan, domainStart, domainEnd]
    );
    const margins = useMemo(() => {
        if (!fullWidths) return DESKTOP_MARGINS;
        return compactMargins(
            shownWidths ? widest(fullWidths, shownWidths) : fullWidths
        );
    }, [fullWidths, shownWidths]);
    const reserve =
        compact && lifetime ? lifetimeReserve(lifetimeText(lifetime)) : 0;

    return (
        <Box
            ref={ref}
            sx={{
                width: '100%',
                minWidth: 0,
                position: 'relative',
                ...(fill && {
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 1 0',
                    minHeight: 0,
                }),
            }}
        >
            {loading && stackIds.length === 0 && (
                <Skeleton
                    variant="rounded"
                    width="100%"
                    height={emptyHeight + TIME_AXIS_HEIGHT}
                />
            )}
            {!loading && stackIds.length === 0 && (
                <Box
                    sx={{
                        height: emptyHeight + TIME_AXIS_HEIGHT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography
                        sx={{ fontSize: '13px', color: 'text.disabled' }}
                    >
                        No price data for this time range
                    </Typography>
                </Box>
            )}
            {compact &&
                mainSeries.length === 0 &&
                stackIds.includes('probability') && (
                    <ChartValuesStripPlaceholder rows={pendingValueRows} />
                )}
            {compact && mainSeries.length > 0 && (
                <ChartValuesStrip
                    series={mainSeries}
                    domain={[domain[0].getTime(), domain[1].getTime()]}
                    loaded={[bounds[0].getTime(), bounds[1].getTime()]}
                    cursor={cursor?.t ?? null}
                    rangeLabel={
                        rangeLabel ??
                        formatSpan(domain[1].getTime() - domain[0].getTime())
                    }
                    minRows={pendingValueRows}
                />
            )}
            {stackIds.length > 0 && (
                <ViewportContext.Provider value={viewport}>
                    <Box
                        ref={panelsBox.ref}
                        sx={fill ? { flex: '1 1 0', minHeight: 0 } : undefined}
                    >
                        {stackIds.map((id, i) => {
                            const isLast = i === stackIds.length - 1;
                            const box = layout.boxes[i];
                            const height =
                                box.height + (isLast ? TIME_AXIS_HEIGHT : 0);
                            const panel = planById.get(id);
                            if (!panel) {
                                return (
                                    <Box key={id} sx={panelGapSx(i)}>
                                        <PanelPlaceholder
                                            id={id}
                                            height={
                                                (shownStrips[id] ?? 0) + height
                                            }
                                        />
                                    </Box>
                                );
                            }
                            return (
                                <Box key={id} sx={panelGapSx(i)}>
                                    {panelStrips[id] && (
                                        <PanelStrip
                                            id={id}
                                            onHeight={setStripHeight}
                                        >
                                            {panelStrips[id]}
                                        </PanelStrip>
                                    )}
                                    {width > 0 ? (
                                        <Panel
                                            plan={panel}
                                            width={width}
                                            height={height}
                                            showTimeAxis={isLast}
                                            margins={margins}
                                            timeAxisReserve={
                                                isLast ? reserve : 0
                                            }
                                        />
                                    ) : (
                                        // Its height before the width is
                                        // measured, so nothing below moves.
                                        <Box sx={{ height }} />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </ViewportContext.Provider>
            )}
            {width > 0 &&
                planById.has(stackIds[stackIds.length - 1]) &&
                lifetime && (
                    <LifetimeBar
                        lifetime={lifetime}
                        shown={
                            zoomed
                                ? [domain[0].getTime(), domain[1].getTime()]
                                : null
                        }
                        left={margins.left}
                        right={margins.right}
                        bottom={TIME_AXIS_HEIGHT + 1}
                        sink={overflow}
                    />
                )}
        </Box>
    );
}
